import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "./api";
import { getDeviceId } from "./storage";

export interface LmsPlayer { id: string; name: string; alive: boolean; eliminated_gw: number | null; reason: "lost" | "drew" | "no_pick" | null; picked: boolean; pick: string | null; used_teams: string[] }
export interface LmsGameweek { number: number; picks: Record<string, string>; results: Record<string, "W" | "D" | "L">; eliminated: Record<string, string>; rollover: boolean }
export interface LmsComp {
  id: string; name: string; max_players: number; prize_pot: number; teams: string[]; gameweek: number;
  window: "pending" | "open" | "closed"; status: "active" | "finished"; winner_id: string | null; is_admin: boolean; has_pin: boolean; rollovers: number; deadline: string | null; server_now: string;
  players: LmsPlayer[]; history: LmsGameweek[];
  me: { id: string; name: string; alive: boolean; pick: string | null; used_teams: string[] } | null;
}

const K = "dither.lms";
type Saved = { id: string; name: string; admin?: string };

export const lmsStore = {
  all: (): Saved[] => { try { return JSON.parse(localStorage.getItem(K) || "[]"); } catch { return []; } },
  save(entry: Saved) {
    const list = lmsStore.all();
    const prev = list.find((x) => x.id === entry.id);
    localStorage.setItem(K, JSON.stringify([{ ...prev, ...entry, admin: entry.admin || prev?.admin }, ...list.filter((x) => x.id !== entry.id)]));
  },
  admin: (id: string) => lmsStore.all().find((x) => x.id === id)?.admin,
};

export const adminHeaders = (id: string) => ({ headers: { "x-admin-token": lmsStore.admin(id) || "" } });
export const joinLink = (id: string) => `${window.location.origin}/lms/${id}`;
export const adminLink = (id: string, token: string) => `${window.location.origin}/lms/${id}/admin/${token}`;
export const standingsShareUrl = (id: string) => `${process.env.REACT_APP_BACKEND_URL}/api/share/lms/${id}`;
export const standingsCardUrl = (id: string, v: string) => `${process.env.REACT_APP_BACKEND_URL}/api/share/lms/${id}/card.png?v=${v}`;
export const formatPot = (n: number) => `£${n.toLocaleString("en-GB", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

export function useLms(id: string) {
  const [comp, setComp] = useState<LmsComp | null>(null);
  const [error, setError] = useState("");
  const [live, setLive] = useState(false);
  const stop = useRef(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/lms/${id}`, { params: { device_id: getDeviceId() }, ...adminHeaders(id) });
      setComp(data);
    } catch (e: any) {
      if (e.response?.status === 404) { stop.current = true; setError(apiError(e)); }
    }
  }, [id]);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let retry: ReturnType<typeof setTimeout>;
    let attempts = 0;
    stop.current = false;
    const url = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws")}/api/lms/${id}/ws`;
    const connect = () => {
      if (stop.current) return;
      ws = new WebSocket(url);
      ws.onopen = () => { attempts = 0; setLive(true); load(); };
      ws.onmessage = () => load();
      ws.onclose = () => { setLive(false); if (!stop.current) retry = setTimeout(connect, Math.min(10000, 1000 * 2 ** attempts++)); };
    };
    load();
    connect();
    const ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send("ping"), 25000);
    const fallback = setInterval(() => !stop.current && document.visibilityState === "visible" && load(), 20000);
    return () => { stop.current = true; clearTimeout(retry); clearInterval(ping); clearInterval(fallback); ws?.close(); };
  }, [id, load]);

  return { comp, setComp, error, load, live };
}
